/**
 * Boîte à outils — commandes partagées par les outils : interrupteur en
 * pastille, bouton icône, compteur ±, curseur nommé, choix segmenté, boutons
 * d'action. Cibles de 44 px, thème clair et sombre (pas de `bg-primary-600`
 * plein : `bg-primary-500 text-white`, cf. CLAUDE.md).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@jeraisonne/ui';

export const PRIMARY_BUTTON =
  // thème sombre : primary-400/600 y sont des bleus CLAIRS (texte blanc illisible) :
  // au survol on assombrit le bleu plein
  'bg-primary-500 hover:bg-primary-600 ocean:hover:bg-primary-500 ocean:hover:brightness-90 min-h-11 rounded-xl px-4 text-sm font-bold text-white active:scale-[0.98]';
export const SECONDARY_BUTTON =
  'border-ink-200 text-ink-700 hover:bg-ink-50 min-h-11 rounded-xl border px-4 text-sm font-semibold active:scale-[0.98]';

export function ToggleChip({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'min-h-11 min-w-11 rounded-xl border px-3 text-sm font-semibold transition-colors active:scale-[0.98]',
        on
          ? 'border-primary-500 bg-primary-50 text-primary-700'
          : 'border-ink-200 text-ink-600 hover:bg-ink-50',
      )}
    >
      {children}
    </button>
  );
}

export function IconButton({
  label,
  onClick,
  children,
  disabled = false,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="border-ink-200 text-ink-700 hover:bg-ink-50 flex min-h-11 min-w-11 items-center justify-center rounded-xl border active:scale-[0.98] disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/**
 * Compteur « − valeur + » ; aria « Lignes : moins » / « Lignes : plus ».
 * `labelAbove` : l'étiquette passe au-dessus, sur toute la largeur (une
 * étiquette longue ne tient pas entre deux boutons de 44 px à 375 px).
 */
export function Stepper({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  format = (v) => String(v),
  labelAbove = false,
  srLabel,
}: {
  label: string;
  /** nom lu par le lecteur d'écran à la place de `label` (formule écrite en mots) */
  srLabel?: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
  labelAbove?: boolean;
}) {
  return (
    <div
      className={cn(
        'border-ink-100 rounded-2xl border px-1 py-1',
        labelAbove
          ? 'flex flex-wrap items-center justify-between'
          : 'flex items-center justify-between',
      )}
    >
      {labelAbove && (
        <span className="text-ink-500 block w-full pt-0.5 text-center text-xs">{label}</span>
      )}
      <button
        type="button"
        aria-label={`${srLabel ?? label} : moins`}
        disabled={value - step < min - 1e-9}
        onClick={() => onChange(Math.max(min, value - step))}
        className="text-ink-700 hover:bg-ink-100 flex min-h-11 min-w-11 items-center justify-center rounded-xl disabled:opacity-40"
      >
        <Minus className="h-5 w-5" aria-hidden />
      </button>
      <span className="min-w-0 px-1 text-center">
        {!labelAbove && <span className="text-ink-500 block text-xs">{label}</span>}
        <span className="text-ink-900 font-display text-lg font-bold tabular-nums">
          {format(value)}
        </span>
      </span>
      <button
        type="button"
        aria-label={`${srLabel ?? label} : plus`}
        disabled={value + step > max + 1e-9}
        onClick={() => onChange(Math.min(max, value + step))}
        className="text-ink-700 hover:bg-ink-100 flex min-h-11 min-w-11 items-center justify-center rounded-xl disabled:opacity-40"
      >
        <Plus className="h-5 w-5" aria-hidden />
      </button>
    </div>
  );
}

/** Curseur avec son nom et sa valeur au-dessus. */
export function ParamSlider({
  name,
  ariaLabel,
  value,
  min,
  max,
  step,
  onChange,
  format = (v) => String(v),
  color,
}: {
  name: ReactNode;
  ariaLabel: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
  /** pastille de couleur (lien avec la figure) */
  color?: string;
}) {
  const decimals = (String(step).split('.')[1] ?? '').length;
  return (
    <label className="block">
      <span className="text-ink-700 flex items-baseline justify-between gap-2 text-sm font-semibold">
        <span className="flex items-center gap-2">
          {color && (
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ background: color }}
              aria-hidden
            />
          )}
          {name}
        </span>
        <span className="font-display text-ink-900 tabular-nums">{format(value)}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={ariaLabel}
        aria-valuetext={format(value)}
        onChange={(e) => onChange(Number(Number(e.target.value).toFixed(decimals)))}
        className="accent-primary-600 h-11 w-full"
      />
    </label>
  );
}

/** Choix exclusif en onglets (« Radians | Degrés »). */
export function Segmented<T extends string>({
  ariaLabel,
  value,
  options,
  onChange,
}: {
  ariaLabel: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="bg-ink-100 inline-flex flex-wrap rounded-xl p-1 text-sm font-semibold"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'min-h-11 min-w-11 rounded-lg px-3 transition-colors',
            value === o.value ? 'text-ink-900 bg-white shadow-sm' : 'text-ink-500',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Encadré d'explication sous la figure (« L'égalité est vraie : … »). */
export function Note({
  tone = 'neutral',
  children,
  live = true,
}: {
  tone?: 'neutral' | 'success' | 'error';
  children: ReactNode;
  live?: boolean;
}) {
  return (
    <p
      aria-live={live ? 'polite' : undefined}
      className={cn(
        'text-ink-800 rounded-2xl px-3 py-2 text-sm',
        tone === 'success' && 'bg-success-50',
        tone === 'error' && 'bg-error-50',
        tone === 'neutral' && 'bg-ink-50',
      )}
    >
      {children}
    </p>
  );
}

const show = (v: number) => String(v).replace('.', ',').replace('-', '−');

/**
 * Nombre saisi au clavier (entier, ou décimal si `decimals` > 0), avec − et +
 * pour les petits pas. aria : « Valeur de a », « a : moins », « a : plus ».
 */
export function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  decimals = 0,
  snap,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  decimals?: number;
  /** valeur arrondie à ce pas (0,5 : demi-unités) */
  snap?: number;
}) {
  const [text, setText] = useState(show(value));
  const typing = useRef(false);
  // la valeur peut changer d'ailleurs (bouton, défi) : on suit, sauf pendant la frappe
  useEffect(() => {
    if (!typing.current) setText(show(value));
  }, [value]);
  const clampRound = (v: number) => {
    const r = snap ? Math.round(v / snap) * snap : v;
    return Math.min(max, Math.max(min, Number(r.toFixed(decimals))));
  };
  const set = (v: number) => {
    const c = clampRound(v);
    setText(show(c));
    onChange(c);
  };
  const pattern = decimals > 0 ? /^-?\d+([.,]\d+)?$/ : /^-?\d+$/;
  return (
    <div className="border-ink-100 flex items-center justify-between rounded-2xl border px-1 py-1">
      <button
        type="button"
        aria-label={`${label} : moins`}
        onClick={() => set(value - step)}
        className="text-ink-700 hover:bg-ink-100 flex min-h-11 min-w-11 items-center justify-center rounded-xl"
      >
        <Minus className="h-5 w-5" aria-hidden />
      </button>
      <label className="min-w-0 flex-1 text-center">
        <span className="text-ink-500 block text-xs">{label}</span>
        <input
          type="text"
          inputMode={decimals > 0 ? 'decimal' : 'numeric'}
          value={text}
          aria-label={`Valeur de ${label}`}
          onChange={(e) => {
            const t = e.target.value.replace('−', '-');
            setText(t);
            // « -0 », « -0, » : un nombre négatif en cours de frappe, pas encore 0
            if (pattern.test(t) && !/^-0*([.,]0*)?$/.test(t))
              onChange(clampRound(Number(t.replace(',', '.'))));
          }}
          onFocus={() => {
            typing.current = true;
          }}
          onBlur={() => {
            typing.current = false;
            setText(show(value));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setText(show(value));
          }}
          className="text-ink-900 font-display w-full bg-transparent text-center text-lg font-bold tabular-nums outline-none"
        />
      </label>
      <button
        type="button"
        aria-label={`${label} : plus`}
        onClick={() => set(value + step)}
        className="text-ink-700 hover:bg-ink-100 flex min-h-11 min-w-11 items-center justify-center rounded-xl"
      >
        <Plus className="h-5 w-5" aria-hidden />
      </button>
    </div>
  );
}
